<template>
  <LoadingSpinner :loading="loading"> </LoadingSpinner>

  <div class="container mt-4 mb-5">
    <div style="max-width:760px">
      <h3 class="mb-1">Abonnement Config</h3>
      <div style="font-style: italic; color:var(--bo-muted)">
        SaaS-abonnement/lisensfakturering: prøveperiode, modulpriser, purring — og Dintero-kontoen
        merchantene trekkes fra. Lagres i Couchbase (ikke i kildekode/git); endringer tas i bruk uten
        restart. Beløp er i <strong>øre eks. mva</strong> (25 % mva legges på ved belastning).
        <br><br>
        De to seksjonene er <strong>to dokumenter</strong> og har derfor hver sin lagre-knapp: vilkårene
        under er forretningsregler, kontoen nederst er legitimasjon. Én felles lagring kunne lykkes
        halvveis, og en side som later som det er atomisk ville løyet om noe som gjelder penger.
      </div>

      <config-lock ref="lock" :configured="isConfigured">
      <h5 class="mt-4 mb-1">Prøveperiode &amp; kortregistrering</h5>
      <div class="row g-3 mt-0">
        <div class="col-md-4">
          <label class="form-label mb-1 sub-label">Prøveperiode (dager)</label>
          <input class="form-control" type="number" min="0" v-model.number="form.trialDays" />
        </div>
        <div class="col-md-4">
          <label class="form-label mb-1 sub-label">Frist for start av prøveperiode (dager)</label>
          <input class="form-control" type="number" min="0" v-model.number="form.trialStartBackstopDays" />
          <span style="color:var(--bo-muted); font-size:0.78rem">
            Prøveperioden starter den dagen merchanten kan selge (meny + VIVA). Denne fristen starter den
            uansett, slik at en konto som aldri blir ferdig ikke blir gratis for alltid.
          </span>
        </div>
        <div class="col-md-4">
          <label class="form-label mb-1 sub-label">Vault-beløp (øre)</label>
          <input class="form-control" type="number" min="0" v-model.number="form.vaultAmountMinor" />
          <span style="color:var(--bo-muted); font-size:0.78rem">0 = ren kortlagring (ingen reservasjon).</span>
        </div>
      </div>

      <h5 class="mt-4 mb-1">Priser per modul (øre eks. mva)</h5>
      <p style="color:var(--bo-muted); font-size:0.82rem" class="mb-2">
        Disse prisene er eneste kilde: de brukes både til å fakturere merchants og til å vise prisene på
        cagista.com. <strong>0 betyr ikke satt</strong> — da nekter trekket å gå, og prissiden viser
        «pris på forespørsel» i stedet for et tall.
      </p>
      <div class="row g-3 mt-0">
        <div class="col-md-4">
          <label class="form-label mb-1 sub-label">POS (per terminal)</label>
          <input class="form-control" type="number" min="0" v-model.number="form.pricePosMinor" />
          <span style="color:var(--bo-muted); font-size:0.78rem">49900 = kr 499</span>
        </div>
        <div class="col-md-4">
          <label class="form-label mb-1 sub-label">SMS (per melding)</label>
          <input class="form-control" type="number" min="0" v-model.number="form.smsPriceMinor" />
          <span style="color:var(--bo-muted); font-size:0.78rem">100 = kr 1,00. Måles fra faktisk forbruk og
          faktureres etterskuddsvis sammen med lisensen.</span>
        </div>
        <div class="col-md-4">
          <label class="form-label mb-1 sub-label">Kiosk (per kiosk)</label>
          <input class="form-control" type="number" min="0" v-model.number="form.priceKioskMinor" />
        </div>
        <div class="col-md-4">
          <label class="form-label mb-1 sub-label">KDS (per terminal)</label>
          <input class="form-control" type="number" min="0" v-model.number="form.priceKdsMinor" />
        </div>
        <div class="col-md-4">
          <label class="form-label mb-1 sub-label">Takeaway (per lokasjon)</label>
          <input class="form-control" type="number" min="0" v-model.number="form.priceTakeawayMinor" />
        </div>
        <div class="col-md-4">
          <label class="form-label mb-1 sub-label">QR (per lokasjon)</label>
          <input class="form-control" type="number" min="0" v-model.number="form.priceQrMinor" />
        </div>
        <div class="col-md-4">
          <label class="form-label mb-1 sub-label">Bordbooking (per lokasjon)</label>
          <input class="form-control" type="number" min="0" v-model.number="form.priceBookingMinor" />
        </div>
        <div class="col-md-4">
          <label class="form-label mb-1 sub-label">Kundeklarskjerm/ODS (per lokasjon, ubegrenset skjermer)</label>
          <input class="form-control" type="number" min="0" v-model.number="form.priceOdsMinor" />
        </div>
      </div>

      <h5 class="mt-4 mb-1">Purring (dunning)</h5>
      <div class="row g-3 mt-0">
        <div class="col-md-4">
          <label class="form-label mb-1 sub-label">Maks forsøk</label>
          <input class="form-control" type="number" min="1" v-model.number="form.dunningMaxAttempts" />
        </div>
        <div class="col-md-4">
          <label class="form-label mb-1 sub-label">Dager mellom forsøk</label>
          <input class="form-control" type="number" min="1" v-model.number="form.dunningRetryDays" />
        </div>
      </div>

      <h5 class="mt-4 mb-1">Betalingsleverandør</h5>
      <div class="row g-3 mt-0">
        <div class="col-md-6">
          <label class="form-label mb-1 sub-label">Hvem tar betalt for lisensen</label>
          <select class="form-select" v-model="form.billingPsp">
            <option value="VIVA">Viva</option>
            <option value="SWEDBANK_PAY">Swedbank Pay</option>
            <option value="DINTERO">Dintero</option>
          </select>
          <div class="sub-hint">Endres uten omstart. Kortene som allerede er lagret hos den ene virker
            ikke hos den andre — en merchant som bytter må legge inn kortet på nytt.</div>
          <div class="sub-hint" v-if="form.billingPsp === 'VIVA'">
            Verifisert ende til ende i testmiljøet 16.09.2026: kortlagring for kr 0, og et trekk uten
            kunde til stede og uten 3-D Secure. Viva har bekreftet skriftlig at gjentakende belastninger
            ikke krever SCA. <strong>Recurring må være slått på for kontoen</strong> — gjøres av Viva eller
            i merchant-kontoen selv, og er det <em>eneste</em> som skiller en konto som virker fra en som
            ikke gjør det. Er den ikke på, lagres kortet og hver månedsbelastning feiler.
          </div>
        </div>
      </div>

      <!-- Only the chosen provider's fields are shown. Both sets are kept and saved either way: a merchant
           mid-trial on the old provider still has to be billable while the switch is being reconsidered. -->
      <template v-if="form.billingPsp === 'VIVA'">
        <h6 class="mt-3 mb-1">Viva</h6>
        <div class="row g-3 mt-0">
          <div class="col-md-6">
            <label class="form-label mb-1 sub-label">Return-URL (Backoffice /billing)</label>
            <input class="form-control" type="text" v-model="form.vivaReturnUrl" />
            <div class="sub-hint"><strong>Viva leser ikke denne.</strong> Til forskjell fra de to andre tar
              Viva ingen retur-URL i ordren — den henger på <em>betalingskilden</em>, og settes i Vivas eget
              dashbord. Verdien her er bare notatet over hvilken URL kilden skal peke på; det er
              <code>sourceCode</code> på kontoen under som avgjør om merchanten kommer tilbake i det hele
              tatt. Mangler den, ender de på Vivas kvitteringsside.</div>
          </div>
          <div class="col-md-6">
            <label class="form-label mb-1 sub-label">Callback-URL (webhook)</label>
            <input class="form-control" type="text" v-model="form.vivaCallbackUrl" />
            <div class="sub-hint">Må være offentlig nåbar, og må registreres i Viva — Viva nekter å
              registrere en URL som ikke svarer på GET med verifiseringsnøkkelen. Den ligger i
              <code>VIVA Partner Config</code>, samme nøkkel som kassasystembutikkens webhook bruker.
              I produksjon <code>https://cagista.services/api/public/subscriptionVivaCallback</code>.</div>
          </div>
        </div>
      </template>

      <template v-else-if="form.billingPsp === 'SWEDBANK_PAY'">
        <h6 class="mt-3 mb-1">Swedbank Pay</h6>
        <div class="row g-3 mt-0">
          <div class="col-md-6">
            <label class="form-label mb-1 sub-label">Return-URL (Backoffice /billing)</label>
            <input class="form-control" type="text" v-model="form.swedbankPayReturnUrl" />
            <div class="sub-hint">Der merchanten havner etter å ha lagt inn kortet. I produksjon
              <code>https://backoffice.cagista.com/billing</code>, som også er standardverdien.</div>
          </div>
          <div class="col-md-6">
            <label class="form-label mb-1 sub-label">Callback-URL (webhook)</label>
            <input class="form-control" type="text" v-model="form.swedbankPayCallbackUrl" />
            <div class="sub-hint">Må være offentlig nåbar: Swedbank Pay kaller den fra sine servere.
              I produksjon <code>https://cagista.services/api/public/subscriptionSwedbankPayCallback</code>,
              som også er standardverdien. Dette er det eneste i integrasjonen som ikke er testet ennå.</div>
          </div>
        </div>
      </template>

      <template v-else>
        <h6 class="mt-3 mb-1">Dintero</h6>
        <div class="row g-3 mt-0">
          <div class="col-md-6">
            <label class="form-label mb-1 sub-label">Return-URL (Backoffice /billing)</label>
            <input class="form-control" type="text" v-model="form.dinteroReturnUrl" />
          </div>
          <div class="col-md-6">
            <label class="form-label mb-1 sub-label">Callback-URL (webhook)</label>
            <input class="form-control" type="text" v-model="form.dinteroCallbackUrl" />
          </div>
        </div>
      </template>

      <h5 class="mt-4 mb-1">Pilot</h5>
      <!-- The project's own switch: backoffice.css sets `.form-switch input { display:none }`
           and draws the toggle from the <i>, so Bootstrap's form-check markup renders nothing. -->
      <div class="form-switch-wrapper" style="clear:both; overflow:auto; float:none">
        <label class="form-switch">
          <div class="form-switch-container">
            <input type="checkbox" v-model="form.manualApproval" />
            <i></i>
            <span>Manuell godkjenning av belastninger
          <span style="color:var(--bo-muted); font-size:0.82rem">— på = forfalte trekk må godkjennes i /tastyadmin → Abonnementer før de belastes; av = full automatikk.</span></span>
          </div>
        </label>
      </div>

      <div class="mt-4 d-flex align-items-center gap-3">
        <button class="bo_btn" @click="save" :disabled="saving">{{ saving ? 'Lagrer…' : 'Lagre' }}</button>
        <span v-if="message" :class="error ? 'text-danger' : 'text-success'">{{ message }}</span>
      </div>
      </config-lock>

      <h3 class="mt-5 mb-1">{{ accountHeading }}</h3>
      <div style="font-style: italic; color:var(--bo-muted)">
        CAGISTAs egen konto for abonnement (kort-på-fil). Lagres som SYSTEM / SUBSCRIPTION.
        Kassasystembutikken har sin <em>egen</em> konto på SYSTEM / DEVICE_STORE og går ikke gjennom
        denne — også når begge står på Viva. Butikken tar imot penger fra en utstyrskjøper, denne
        belaster en merchant.
      </div>

      <!-- The Viva account. TWO credential pairs on one document, and both are needed:
           clientId/clientSecret mint the OAuth bearer that creates the card-verification order, while
           merchantId/apiKey are the Basic pair the LEGACY API wants for the recurring charge -- the same
           pair the device store refunds with, the account acting as itself. Viva never merged the two API
           generations, so filling in only the pair that stores the card gives a merchant whose card is on
           file and cannot be charged. -->
      <template v-if="form.billingPsp === 'VIVA'">
        <config-lock ref="vivaAccountLock" :configured="vivaAccountIsConfigured">
        <div class="row g-3 mt-0">
          <div class="col-md-6">
            <label class="form-label mb-1 sub-label">clientId</label>
            <input class="form-control" type="text" v-model="vivaAccount.clientId" />
            <div class="sub-hint">OAuth-paret. Brukes til å opprette kortlagringen.</div>
          </div>
          <div class="col-md-6">
            <label class="form-label mb-1 sub-label">clientSecret</label>
            <input class="form-control" type="password" autocomplete="new-password"
                   v-model="vivaAccount.clientSecret" />
          </div>
          <div class="col-md-6">
            <label class="form-label mb-1 sub-label">merchantId</label>
            <input class="form-control" type="text" v-model="vivaAccount.merchantId" />
            <div class="sub-hint">Basic-paret, mot det eldre API-et — samme legitimasjon
              kassasystembutikken refunderer med. Uten disse to lagres kortet, men månedsbelastningen kan
              ikke kjøres.</div>
          </div>
          <div class="col-md-6">
            <label class="form-label mb-1 sub-label">apiKey</label>
            <input class="form-control" type="password" autocomplete="new-password"
                   v-model="vivaAccount.apiKey" />
          </div>
          <div class="col-md-6">
            <label class="form-label mb-1 sub-label">sourceCode</label>
            <input class="form-control" type="text" v-model="vivaAccount.sourceCode" />
            <div class="sub-hint">Betalingskilden som bærer return-URL-en. Egen kilde for abonnement —
              kassasystembutikkens kilde sender merchanten til <code>/store/vivaPaymentStatus</code>.</div>
          </div>
        </div>
        <div class="mt-3">
          <button class="bo_btn" :disabled="savingVivaAccount" @click="saveVivaAccount">
            {{ savingVivaAccount ? 'Lagrer …' : 'Lagre konto' }}
          </button>
          <span v-if="vivaAccountMessage" class="ms-3"
                :class="vivaAccountError ? 'message-error' : 'message-info'">{{ vivaAccountMessage }}</span>
        </div>
        </config-lock>
      </template>

      <!-- The Swedbank Pay account, shown when it is the chosen provider. Same SYSTEM / SUBSCRIPTION key as
           the Dintero one, so switching provider does not move where credentials live — only which document
           is read. The two forms are separate because the fields are: a bearer token and a payeeId against
           an OAuth client pair. -->
      <template v-else-if="form.billingPsp === 'SWEDBANK_PAY'">
        <config-lock ref="swpAccountLock" :configured="swpAccountIsConfigured">
        <div class="row g-3 mt-0">
          <div class="col-md-6">
            <label class="form-label mb-1 sub-label">API base-URL</label>
            <input class="form-control" type="text" v-model="swpAccount.swedbankPayBaseApiUrl"
                   placeholder="https://api.externalintegration.payex.com" />
            <div class="sub-hint">
              TEST: <code>https://api.externalintegration.payex.com</code><br>
              PROD: <code>https://api.payex.com</code>
            </div>
          </div>
          <div class="col-md-6">
            <label class="form-label mb-1 sub-label">payeeId</label>
            <input class="form-control" type="text" v-model="swpAccount.payeeId" />
          </div>
          <div class="col-md-6">
            <label class="form-label mb-1 sub-label">payeeName</label>
            <input class="form-control" type="text" v-model="swpAccount.payeeName" />
          </div>
          <div class="col-md-6">
            <label class="form-label mb-1 sub-label">accessToken</label>
            <input class="form-control" type="password" autocomplete="new-password"
                   v-model="swpAccount.accessToken" />
          </div>
        </div>
        <div class="mt-3">
          <button class="bo_btn" :disabled="savingSwpAccount" @click="saveSwpAccount">
            {{ savingSwpAccount ? 'Lagrer …' : 'Lagre konto' }}
          </button>
          <span v-if="swpAccountMessage" class="ms-3"
                :class="swpAccountError ? 'message-error' : 'message-info'">{{ swpAccountMessage }}</span>
        </div>
        </config-lock>
      </template>

      <template v-else>

      <config-lock ref="accountLock" :configured="accountIsConfigured">
      <div class="row g-3 mt-0">
        <div class="col-md-6">
          <label class="form-label mb-1 sub-label">account_id (f.eks T11112936)</label>
          <input class="form-control" type="text" v-model="account.accountId" />
        </div>
        <div class="col-md-6">
          <label class="form-label mb-1 sub-label">profile_id</label>
          <input class="form-control" type="text" v-model="account.checkoutProfileId" />
        </div>
        <div class="col-md-6">
          <label class="form-label mb-1 sub-label">default_payment_type (f.eks payex.creditcard)</label>
          <input class="form-control" type="text" v-model="account.defaultPaymentType" />
        </div>
        <div class="col-md-6">
          <label class="form-label mb-1 sub-label">primary (temafarge, f.eks #f0c7a9)</label>
          <input class="form-control" type="text" v-model="account.themePrimaryColor" />
        </div>
        <div class="col-md-6">
          <label class="form-label mb-1 sub-label">client_id</label>
          <input class="form-control" type="text" v-model="account.clientId" />
        </div>
        <div class="col-md-6">
          <label class="form-label mb-1 sub-label">client_secret</label>
          <input class="form-control" type="password" autocomplete="new-password" v-model="account.clientSecret" />
          <span style="color:var(--bo-muted); font-size:0.78rem">Dintero Basic Auth Token = Base64(client_id:client_secret).</span>
        </div>
      </div>

      <div class="mt-4 d-flex align-items-center gap-3">
        <button class="bo_btn" @click="saveAccount" :disabled="savingAccount">{{ savingAccount ? 'Lagrer…' : 'Lagre konto' }}</button>
        <span v-if="accountMessage" :class="accountError ? 'text-danger' : 'text-success'">{{ accountMessage }}</span>
      </div>
      </config-lock>
      </template>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import ConfigLock from "@/components/other/ConfigLock";
import SettingsService from "@/services/settings/SettingsService";

export default {
  name: "SubscriptionConfig",
  components: { LoadingSpinner, ConfigLock },
  computed: {
    // Already configured (→ starts locked): the chosen provider's URLs have been set. Prices/trial always
    // carry defaults, so they can't distinguish a saved config from a fresh one — the URLs can.
    isConfigured() {
      if (this.form.billingPsp === "VIVA") {
        return !!(this.form.vivaReturnUrl || this.form.vivaCallbackUrl);
      }
      if (this.form.billingPsp === "SWEDBANK_PAY") {
        return !!(this.form.swedbankPayReturnUrl || this.form.swedbankPayCallbackUrl);
      }
      return !!(this.form.dinteroReturnUrl || this.form.dinteroCallbackUrl);
    },
    // Named per provider rather than falling through to Dintero. A heading is what tells the reader which
    // account they are about to overwrite, and with three providers a v-else would have labelled Viva's
    // form «Dintero-konto».
    accountHeading() {
      if (this.form.billingPsp === "VIVA") return "Viva-konto";
      if (this.form.billingPsp === "SWEDBANK_PAY") return "Swedbank Pay-konto";
      return "Dintero-konto";
    },
    // Credentials were loaded from the server → the section starts locked.
    accountIsConfigured() { return !!(this.account.clientId || this.account.accountId); },
    swpAccountIsConfigured() { return !!(this.swpAccount.accessToken || this.swpAccount.payeeId); },
    vivaAccountIsConfigured() { return !!(this.vivaAccount.clientId || this.vivaAccount.merchantId); }
  },
  data() {
    return {
      loading: true,
      form: {
        trialDays: 30, trialStartBackstopDays: 90, vaultAmountMinor: 0,
        billingPsp: "VIVA",
        dinteroReturnUrl: "", dinteroCallbackUrl: "",
        swedbankPayReturnUrl: "", swedbankPayCallbackUrl: "",
        vivaReturnUrl: "", vivaCallbackUrl: "",
        dunningMaxAttempts: 4, dunningRetryDays: 2,
        pricePosMinor: 0, priceKioskMinor: 0, priceKdsMinor: 0,
        priceTakeawayMinor: 0, priceQrMinor: 0, priceBookingMinor: 0, priceOdsMinor: 0, smsPriceMinor: 0,
        manualApproval: true
      },
      saving: false,
      message: "",
      error: false,

      // CAGISTA's own Dintero account for subscription billing — a SEPARATE Couchbase document
      // (dintero_account at SYSTEM / SUBSCRIPTION) from the config above, which is why it saves on its
      // own button. type/merchant/locationId are re-asserted on save rather than trusted from the form:
      // they are what getDinteroAccount matches on, and a page that let them drift would write an
      // account nothing reads.
      account: {
        // The Couchbase document id, and it MUST be in this object — see the note on vivaAccount below.
        // This form does not currently NEED it: loadAccount assigns the whole server response rather than
        // copying declared keys, so the id survives by accident. That is precisely why it is written out
        // here. Rewrite that loader to the key-by-key pattern the other two use — which is the safer
        // looking one, and was chosen for them deliberately — and this form starts writing a second
        // dintero_account on every save, silently, the way the Viva one did.
        id: "",
        type: "dintero_account",
        merchant: "SYSTEM",
        locationId: "SUBSCRIPTION",
        accountId: "",
        checkoutProfileId: "",
        defaultPaymentType: "",
        themePrimaryColor: "",
        clientId: "",
        clientSecret: ""
      },
      savingAccount: false,
      accountMessage: "",
      accountError: false,

      // The Swedbank Pay equivalent. type/merchant/locationId are re-asserted on save for the same reason
      // the Dintero ones are: they are what getSwedbankPayAccount matches on, and a page that let them
      // drift would write an account nothing reads.
      swpAccount: {
        // The Couchbase document id, and it MUST be in this object.
        // addOrUpdateDocument() generates a fresh UUID when the payload has no "id", so a save
        // without it writes a SECOND account document instead of updating the first. The loader
        // below copies only the keys declared here, so a field missing from this list is silently
        // dropped from the server response -- and this one decides whether the save is an update
        // or an insert. Two credential documents for the same account then sit side by side, and
        // getVivaAccount takes allRows().get(0) with no ORDER BY: which credentials the platform
        // charges with becomes arbitrary. Found in production on 2026-09-17, when a deliberately
        // broken apiKey was saved and the charge went through anyway on the older copy.
        id: "",
        type: "swedbankpay_account",
        merchant: "SYSTEM",
        locationId: "SUBSCRIPTION",
        swedbankPayBaseApiUrl: "",
        payeeId: "",
        payeeName: "",
        accessToken: ""
      },
      savingSwpAccount: false,
      swpAccountMessage: "",
      swpAccountError: false,

      // CAGISTA's own VIVA account for subscriptions — SYSTEM / SUBSCRIPTION. A DIFFERENT document from
      // the device store's at SYSTEM / DEVICE_STORE, even though both are Cagista's own: the store takes
      // money from a hardware buyer, this charges a merchant. type/merchant/locationId are re-asserted on
      // save for the same reason as the other two — they are what getVivaAccount matches on.
      vivaAccount: {
        // The Couchbase document id, and it MUST be in this object.
        // addOrUpdateDocument() generates a fresh UUID when the payload has no "id", so a save
        // without it writes a SECOND account document instead of updating the first. The loader
        // below copies only the keys declared here, so a field missing from this list is silently
        // dropped from the server response -- and this one decides whether the save is an update
        // or an insert. Two credential documents for the same account then sit side by side, and
        // getVivaAccount takes allRows().get(0) with no ORDER BY: which credentials the platform
        // charges with becomes arbitrary. Found in production on 2026-09-17, when a deliberately
        // broken apiKey was saved and the charge went through anyway on the older copy.
        id: "",
        type: "viva_account",
        merchant: "SYSTEM",
        locationId: "SUBSCRIPTION",
        merchantId: "",
        apiKey: "",
        clientId: "",
        clientSecret: "",
        sourceCode: ""
      },
      savingVivaAccount: false,
      vivaAccountMessage: "",
      vivaAccountError: false
    };
  },
  mounted() {
    this.loadConfig();
    this.loadAccount();
    this.loadSwpAccount();
    this.loadVivaAccount();
  },
  methods: {
    loadConfig() {
      SettingsService.getSubscriptionConfig().then(
        (response) => {
          const data = response.data || {};
          Object.keys(this.form).forEach((k) => {
            if (data[k] !== undefined && data[k] !== null) this.form[k] = data[k];
          });
          this.loading = false;
        },
        (error) => {
          console.log(error);
          this.loading = false;
          this.error = true;
          this.message = this.errMsg(error, "Kunne ikke hente konfigurasjon.");
        }
      );
    },
    save() {
      this.saving = true;
      this.message = "";
      this.error = false;
      SettingsService.saveSubscriptionConfig(this.form).then(
        (response) => {
          this.saving = false;
          const data = response.data || {};
          Object.keys(this.form).forEach((k) => {
            if (data[k] !== undefined && data[k] !== null) this.form[k] = data[k];
          });
          this.error = false;
          this.message = "Lagret ✓ — tas i bruk umiddelbart (ingen restart).";
          this.$refs.lock.relock();   // re-lock after a successful save
        },
        (error) => {
          this.saving = false;
          this.error = true;
          this.message = this.errMsg(error, "Kunne ikke lagre konfigurasjon.");
        }
      );
    },
    loadAccount() {
      SettingsService.getSubscriptionDinteroAccount().then(
        (response) => {
          // An unconfigured account comes back empty; keep the defaults rather than blanking the keys.
          if (response.data) {
            this.account = response.data;
          }
        },
        (error) => {
          console.log(error);
          this.accountError = true;
          this.accountMessage = this.errMsg(error, "Kunne ikke hente Dintero-kontoen.");
        }
      );
    },
    loadSwpAccount() {
      SettingsService.getSubscriptionSwedbankPayAccount().then(
        (response) => {
          // An unconfigured account comes back empty; keep the defaults rather than blanking the keys.
          const a = response.data || {};
          Object.keys(this.swpAccount).forEach((k) => {
            if (a[k] !== undefined && a[k] !== null && a[k] !== "") this.swpAccount[k] = a[k];
          });
        },
        (error) => { console.log(error); }
      );
    },
    saveSwpAccount() {
      this.savingSwpAccount = true;
      this.swpAccountMessage = "";
      this.swpAccountError = false;
      // Re-asserted, not trusted from the form: these three are what getSwedbankPayAccount matches on.
      this.swpAccount.type = "swedbankpay_account";
      this.swpAccount.merchant = "SYSTEM";
      this.swpAccount.locationId = "SUBSCRIPTION";
      // An id of "" is not null, and the backend only generates one when the key is ABSENT --
      // sending an empty string would try to write a document with no id at all. So it is
      // stripped on a first-time save and kept on every later one.
      const swpAccountPayload = { ...this.swpAccount };
      if (!swpAccountPayload.id) delete swpAccountPayload.id;
      SettingsService.editSwedbankPayAccount(swpAccountPayload).then(
        () => {
          this.savingSwpAccount = false;
          this.swpAccountError = false;
          this.swpAccountMessage = "Lagret ✓";
          this.$refs.swpAccountLock && this.$refs.swpAccountLock.relock();
        },
        (error) => {
          this.savingSwpAccount = false;
          this.swpAccountError = true;
          this.swpAccountMessage = this.errMsg(error, "Kunne ikke lagre Swedbank Pay-kontoen.");
        }
      );
    },
    loadVivaAccount() {
      SettingsService.getSubscriptionVivaAccount().then(
        (response) => {
          // An unconfigured account comes back empty; keep the defaults rather than blanking the keys
          // getVivaAccount matches on.
          const a = response.data || {};
          Object.keys(this.vivaAccount).forEach((k) => {
            if (a[k] !== undefined && a[k] !== null && a[k] !== "") this.vivaAccount[k] = a[k];
          });
        },
        (error) => { console.log(error); }
      );
    },
    saveVivaAccount() {
      this.savingVivaAccount = true;
      this.vivaAccountMessage = "";
      this.vivaAccountError = false;
      // Re-asserted, not trusted from the form. SUBSCRIPTION, never DEVICE_STORE: this endpoint writes
      // whichever location the body names, so a stale value here would overwrite the device store's
      // credentials with the subscription ones and stop hardware sales.
      this.vivaAccount.type = "viva_account";
      this.vivaAccount.merchant = "SYSTEM";
      this.vivaAccount.locationId = "SUBSCRIPTION";
      // An id of "" is not null, and the backend only generates one when the key is ABSENT --
      // sending an empty string would try to write a document with no id at all. So it is
      // stripped on a first-time save and kept on every later one.
      const vivaAccountPayload = { ...this.vivaAccount };
      if (!vivaAccountPayload.id) delete vivaAccountPayload.id;
      SettingsService.editVivaAccount(vivaAccountPayload).then(
        () => {
          this.savingVivaAccount = false;
          this.vivaAccountError = false;
          this.vivaAccountMessage = "Lagret ✓";
          this.$refs.vivaAccountLock && this.$refs.vivaAccountLock.relock();
        },
        (error) => {
          this.savingVivaAccount = false;
          this.vivaAccountError = true;
          this.vivaAccountMessage = this.errMsg(error, "Kunne ikke lagre Viva-kontoen.");
        }
      );
    },
    saveAccount() {
      this.savingAccount = true;
      this.accountMessage = "";
      this.accountError = false;
      this.account.type = "dintero_account";
      this.account.merchant = "SYSTEM";
      this.account.locationId = "SUBSCRIPTION";
      // Stripped when empty: "" is not null, and the backend only generates an id when the key is ABSENT.
      const accountPayload = { ...this.account };
      if (!accountPayload.id) delete accountPayload.id;
      SettingsService.editDinteroAccount(accountPayload).then(
        (response) => {
          this.savingAccount = false;
          const documentId = response.data;
          if (documentId) {
            this.account.id = documentId;
            this.accountError = false;
            this.accountMessage = "Lagret ✓";
            this.$refs.accountLock.relock();
          } else {
            this.accountError = true;
            this.accountMessage = "Kunne ikke lagre Dintero-kontoen.";
          }
        },
        (error) => {
          this.savingAccount = false;
          this.accountError = true;
          this.accountMessage = this.errMsg(error, "Kunne ikke lagre Dintero-kontoen.");
        }
      );
    },
    errMsg(error, fallback) {
      const data = error && error.response ? error.response.data : null;
      return (data && data.error) || fallback;
    }
  }
};
</script>

<style scoped>
.sub-label {
  font-size: 0.85rem;
  color: var(--bo-muted);
}
</style>
